import React, { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { Select, Spin } from "antd";
import PageMeta from "@components/common/PageMeta";
import DashboardStats from "@components/home/DashboardStats";
import { DashboardFilterProvider, useDashboardFilter } from "../../context/DashboardFilterContext";
import type { UserType, TimePeriod } from "../../context/DashboardFilterContext";

const BookingConversionChart = lazy(() => import("@components/home/BookingConversionChart"));
const RideVsTimeChart = lazy(() => import("@components/home/RideVsTimeChart"));
const RevenueByRegionChart = lazy(() => import("@components/home/RevenueByRegionChart"));
const PaymentMethodSplitChart = lazy(() => import("@components/home/PaymentMethodSplitChart"));
const ZoneBookingsReport = lazy(() => import("@components/home/ZoneBookingsReport"));
const EmergencyAlerts = lazy(() => import("@components/home/EmergencyAlerts"));
const DriversLiveMap = lazy(() => import("@components/DriversLiveMap/DriversLiveMap"));

const BlockFallback = ({ minHeight = 120 }: { minHeight?: number }) => (
  <div
    className="flex w-full items-center justify-center rounded-2xl border border-gray-100 bg-white/70 dark:border-gray-700 dark:bg-gray-800/40"
    style={{ minHeight }}
  >
    <Spin />
  </div>
);

const userTypeLabels: Record<UserType, string> = {
  customer: "Customer",
  driver: "Driver",
};

const timePeriodLabels: Record<TimePeriod, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

const HomeContent: React.FC = () => {
  const { t } = useTranslation();
  const { userType, timePeriod, setUserType, setTimePeriod } = useDashboardFilter();

  const pageTitle = `${userTypeLabels[userType]} Dashboard (${timePeriodLabels[timePeriod]})`;

  return (
    <>
      <PageMeta title={pageTitle} />
      <div className="space-y-10">
        <div className="dashboard-animate-in -mx-4 rounded-2xl border-l-4 border-[#0056D2] bg-gradient-to-br from-gray-50 via-white to-gray-50/80 px-5 py-6 shadow-sm sm:py-7 md:-mx-6 md:px-7 dark:border-[#569ff7] dark:from-gray-800/50 dark:via-gray-900/40 dark:to-gray-800/30 dark:ring-1 dark:ring-gray-700/50">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl dark:text-white">
                {pageTitle}
              </h2>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                {t("dashboard.welcome")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex rounded-xl bg-white/80 p-1 shadow-sm ring-1 ring-gray-200/60 dark:bg-gray-800/80 dark:ring-gray-600/50">
                {(["customer", "driver"] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setUserType(key)}
                    className={`rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0056D2]/30 focus:ring-offset-2 dark:focus:ring-[#569ff7]/40 ${
                      userType === key
                        ? "bg-[#0056D2] text-white shadow-md dark:bg-[#0056D2]"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                    }`}
                  >
                    {userTypeLabels[key]}
                  </button>
                ))}
              </div>
              <Select
                value={timePeriod}
                onChange={(v) => setTimePeriod(v as TimePeriod)}
                options={[
                  { value: "daily", label: t("dashboard.daily") },
                  { value: "weekly", label: t("dashboard.weekly") },
                  { value: "monthly", label: t("dashboard.monthly") },
                ]}
                className="dashboard-period-select min-w-[120px]"
              />
            </div>
          </div>
        </div>

        {userType === "driver" && (
          <div className="dashboard-animate-in">
            <h3 className="dashboard-section-title mb-4">Live Driver Locations</h3>
            <Suspense fallback={<BlockFallback minHeight={320} />}>
              <DriversLiveMap
                height="min(420px, calc(100vh - 320px))"
                className="border border-gray-200 dark:border-gray-700"
              />
            </Suspense>
          </div>
        )}

        <section className="space-y-10">
          <div className="dashboard-animate-in dashboard-animate-in-delay-1">
            <h3 className="dashboard-section-title">Key metrics</h3>
            <DashboardStats />
          </div>
          <div className="dashboard-animate-in dashboard-animate-in-delay-1 min-w-0">
            <h3 className="dashboard-section-title">Bookings by zone</h3>
            <Suspense fallback={<BlockFallback minHeight={400} />}>
              <ZoneBookingsReport timePeriod={timePeriod} />
            </Suspense>
          </div>
          <div className="dashboard-animate-in dashboard-animate-in-delay-2">
            <h3 className="dashboard-section-title">Analytics</h3>
            <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="min-w-0">
                <Suspense fallback={<BlockFallback minHeight={280} />}>
                  <RideVsTimeChart />
                </Suspense>
              </div>
              <div className="min-w-0">
                <Suspense fallback={<BlockFallback minHeight={280} />}>
                  <RevenueByRegionChart />
                </Suspense>
              </div>
            </div>
            <div className="grid min-w-0 grid-cols-1 gap-6 pt-2 xl:grid-cols-2">
              <div className="min-w-0">
                <Suspense fallback={<BlockFallback minHeight={280} />}>
                  <BookingConversionChart />
                </Suspense>
              </div>
              <div className="min-w-0">
                <Suspense fallback={<BlockFallback minHeight={280} />}>
                  <PaymentMethodSplitChart />
                </Suspense>
              </div>
            </div>
          </div>
          <div className="dashboard-animate-in dashboard-animate-in-delay-3">
            <h3 className="dashboard-section-title">Emergency alerts</h3>
            <Suspense fallback={<BlockFallback minHeight={200} />}>
              <EmergencyAlerts />
            </Suspense>
          </div>
        </section>
      </div>
    </>
  );
};

const Home: React.FC = () => (
  <DashboardFilterProvider>
    <HomeContent />
  </DashboardFilterProvider>
);

export default Home;
